import { type ComponentType, lazy, useEffect, useMemo, useRef } from 'react';

import Suspense from '@atlaskit/react-ufo/suspense';

import {
	useLoggedInHasJira,
	useLoggedInHasJiraSiteD1toD14,
} from '@/components/user-context/helpers/cohorts';
import { useUpdateFeatureGatesClientWithTenantId } from '@/feature-gates/initialization';
import { ClientOnly } from '@/utils/client-only';
import { experiments } from '@/utils/experiment/manifest';
import { EnrollmentType, useExperiment } from '@/utils/experiment/use-experiment';
import { checkFeatureFlagGate } from '@/utils/feature-flags/check-feature-flag';
import { featureFlags } from '@/utils/feature-flags/feature-flag-manifest';

import type { ActivationChecklistPlacementProps } from './activation-checklist-config';

type ActivationChecklistExperimentMountProps = ActivationChecklistPlacementProps & {
	cloudSiteId: string;
};

const LazyActivationChecklistPostOfficeMount = lazy<
	ComponentType<ActivationChecklistPlacementProps>
>(() =>
	import('./activation-checklist-post-office-mount.js').then(
		({ ActivationChecklistPostOfficeMount }) => ({
			default: ActivationChecklistPostOfficeMount,
		}),
	),
);

const useActivationChecklistEnrollment = ({
	cloudSiteId,
	experimentConfig,
}: {
	cloudSiteId: string;
	experimentConfig: typeof experiments.jiraWacActivationChecklist;
}) => {
	const isInTargetSiteCohort = useLoggedInHasJiraSiteD1toD14(cloudSiteId);
	const experimentForTargetSite = useMemo(
		() => ({
			...experimentConfig,
			options: {
				...experimentConfig.options,
				fireExperimentExposure: false,
			},
			targeting: {
				cohort: () => isInTargetSiteCohort,
			},
		}),
		[experimentConfig, isInTargetSiteCohort],
	);
	const exposureMetadata = useMemo(() => ({ tenantId: cloudSiteId, cloudSiteId }), [cloudSiteId]);
	const experiment = useExperiment(experimentForTargetSite, exposureMetadata);
	const updateTenantId = useUpdateFeatureGatesClientWithTenantId();
	const exposureFired = useRef(false);

	useEffect(() => {
		if (exposureFired.current || experiment.type !== EnrollmentType.ENROLLED) {
			return;
		}

		let isCancelled = false;

		const syncTenantContextAndFireExposure = async () => {
			const bootstrapValues =
				typeof window !== 'undefined' ? window.featureGateBootstrapValues : undefined;

			if (bootstrapValues && bootstrapValues.identifiers?.tenantId !== cloudSiteId) {
				await updateTenantId({ cloudId: cloudSiteId });
			}

			if (!isCancelled && !exposureFired.current) {
				experiment.fireExposure();
				exposureFired.current = true;
			}
		};

		void syncTenantContextAndFireExposure();

		return () => {
			isCancelled = true;
		};
	}, [cloudSiteId, experiment, updateTenantId]);

	return experiment;
};

const useActivationChecklistLaunchEnrollment = (cloudSiteId: string) =>
	useActivationChecklistEnrollment({
		cloudSiteId,
		experimentConfig: experiments.jiraWacActivationChecklist,
	});

export const ActivationChecklistMainPlacement = (
	props: ActivationChecklistExperimentMountProps,
) => {
	const experiment = useActivationChecklistLaunchEnrollment(props.cloudSiteId);

	const isEligible = experiment.type === EnrollmentType.ENROLLED && experiment.value === true;

	if (!isEligible) {
		return null;
	}

	return (
		<ClientOnly>
			<Suspense fallback={null} interactionName="wac-activation-checklist-placement">
				<LazyActivationChecklistPostOfficeMount {...props} />
			</Suspense>
		</ClientOnly>
	);
};

export const ActivationChecklistPlacement = (props: ActivationChecklistPlacementProps) => {
	const { cloudSiteId } = props;
	const gateEnabled = checkFeatureFlagGate(featureFlags.jiraWacActivationChecklistGate);
	const loggedInHasJira = useLoggedInHasJira();

	if (gateEnabled !== true || loggedInHasJira !== true) {
		return null;
	}

	if (!cloudSiteId) {
		return null;
	}

	return <ActivationChecklistMainPlacement {...props} cloudSiteId={cloudSiteId} />;
};

ActivationChecklistPlacement.displayName = 'ActivationChecklistPlacement';

export default ActivationChecklistPlacement;
